import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Users, Loader2, ChevronLeft, ChevronRight, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map((s) => Number(s));
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-IN', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map((s) => Number(s));
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const currentYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Colour palette rotated per user row so bars stay distinct even with many users.
const BAR_COLOURS = ['#14b8a6', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e'];

// One row's 6-month mini bar chart. Uses CSS grid + flex bars — no chart lib.
const MiniBars = ({ series, colour }) => {
  const max = Math.max(1, ...series.map((s) => s.total));
  return (
    <div className="flex items-end gap-1 h-10" data-testid="mini-bars">
      {series.map((s) => {
        const pct = Math.round((s.total / max) * 100);
        const h = Math.max(2, pct);
        const label = `${monthLabel(s.month)}: ₹ ${fmtINR(s.total)}`;
        return (
          <div key={s.month} className="flex-1 flex flex-col items-center justify-end min-w-[10px]" title={label}>
            <div
              className="w-full rounded-sm transition-all"
              style={{ height: `${h}%`, backgroundColor: colour, opacity: s.total > 0 ? 1 : 0.15 }}
            />
          </div>
        );
      })}
    </div>
  );
};

const TeamExpensesPage = () => {
  const { authHeader, user } = useAuth();
  const isMaster = !!user?.is_master_admin;

  const [ym, setYm] = useState(currentYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchSummary = useCallback(async () => {
    if (!isMaster) { setLoading(false); return; }
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API}/expenses/team-summary`, {
        headers: authHeader(),
        params: { month: ym },
      });
      setData(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, ym, isMaster]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const users = data?.users || [];
  const grand = data?.grand_total || 0;
  const monthName = useMemo(() => {
    if (!ym) return '';
    return new Date(Date.UTC(...ym.split('-').map((s, i) => (i === 1 ? Number(s) - 1 : Number(s))), 1))
      .toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }, [ym]);

  if (!isMaster) {
    return (
      <div className="p-6" data-testid="team-expenses-forbidden">
        <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-xl p-6 text-center space-y-3">
          <ShieldAlert className="mx-auto text-amber-500" size={28} />
          <h2 className="text-sm font-semibold text-slate-900">Master admin only</h2>
          <p className="text-xs text-slate-500">
            The team expenses view aggregates every staff member&apos;s ledger and is available to the master admin only.
          </p>
          <Link to="/admin/expenses" className="inline-block text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg">
            Back to your expenses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="team-expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Team Expenses</h1>
          <span className="ml-2 text-sm text-slate-500">
            {loading ? '—' : `${users.length} team member${users.length === 1 ? '' : 's'} · ₹ ${fmtINR(grand)}`}
          </span>
        </div>
        <Link to="/admin/expenses" className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-1.5 rounded-lg" data-testid="back-to-expenses">
          ← All expenses
        </Link>
      </div>

      {/* Month selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="month-selector">
        <button
          type="button"
          onClick={() => setYm(shiftMonth(ym, -1))}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
          title="Previous month"
          data-testid="prev-month"
        >
          <ChevronLeft size={16} />
        </button>
        <input
          type="month"
          value={ym}
          onChange={(e) => setYm(e.target.value)}
          className="text-sm bg-slate-50 border border-slate-200 rounded-md px-2 py-1"
          data-testid="month-input"
        />
        <button
          type="button"
          onClick={() => setYm(shiftMonth(ym, 1))}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
          title="Next month"
          data-testid="next-month"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => setYm(currentYM())}
          className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 py-1 rounded-md"
          data-testid="this-month-btn"
        >
          This month
        </button>
        <span className="ml-2 text-xs text-slate-500">Showing <b>{monthName}</b></span>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="team-expenses-error">
          {err}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="team-expenses-empty">
            No expenses recorded in {monthName}. Try a different month.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-slate-600">Team member</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right whitespace-nowrap">Entries</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right whitespace-nowrap">Month total</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">Share</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">Last 6 months</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Top categories</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u, i) => {
                  const colour = BAR_COLOURS[i % BAR_COLOURS.length];
                  const sharePct = grand > 0 ? Math.round((u.month_total / grand) * 100) : 0;
                  const localPart = u.is_system ? 'system' : (u.user_email.split('@')[0] || u.user_email);
                  const topCats = Object.entries(u.by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3);
                  return (
                    <tr key={u.user_email + i} className="hover:bg-slate-50" data-testid={`team-row-${localPart}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: colour }}
                            aria-hidden
                          />
                          <div>
                            <div className="text-slate-900 font-medium">
                              {localPart}
                              {u.is_system && (
                                <span className="ml-1 text-[10px] italic text-slate-400">
                                  (auto-recurring / legacy)
                                </span>
                              )}
                            </div>
                            {!u.is_system && (
                              <div className="text-[11px] text-slate-500">{u.user_email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">{u.entry_count}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap">₹ {fmtINR(u.month_total)}</td>
                      <td className="px-3 py-2 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${sharePct}%`, backgroundColor: colour }}
                              data-testid={`share-${localPart}`}
                            />
                          </div>
                          <span className="text-[11px] text-slate-500 w-9 text-right">{sharePct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 min-w-[180px]">
                        <MiniBars series={u.series || []} colour={colour} />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {topCats.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {topCats.map(([cat, amt]) => (
                              <span
                                key={cat}
                                className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 rounded-full px-2 py-0.5"
                                title={`₹ ${fmtINR(amt)}`}
                              >
                                {cat}
                                <span className="text-slate-500">₹{fmtINR(amt)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold">
                <tr>
                  <td className="px-3 py-2 text-slate-600 text-right" colSpan={2}>Grand total</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap" data-testid="grand-total">₹ {fmtINR(grand)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 px-1">
        Aggregated from the confidential expense ledger. Individual users only see their own entries; this view is
        available to the master admin. The mini bar chart shows the last 6 months of each user&apos;s monthly totals so you
        can spot trends at a glance.
      </p>
    </div>
  );
};

export default TeamExpensesPage;
